const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const asNumber = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

const retentionBelow = (v, threshold) => {
  const n = asNumber(v);
  return n != null && n < threshold;
};

export const fmt = (n) =>
  typeof n === "number" && Number.isFinite(n) ? n.toLocaleString("en-IN") : "—";

export const pct = (v, digits = 1) =>
  typeof v === "number" && Number.isFinite(v) ? `${v.toFixed(digits)}%` : "—";

export const formatLakh = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n >= 100000) return `${(n / 100000).toFixed(2)}L`;
  return fmt(n);
};

export const formatLakhShort = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  return fmt(n);
};

export const formatCompactK = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return fmt(n);
};

const STAGE_ORDER = [
  { key: "primary", match: /primary \(1-5\)/i, label: "Primary", sub: "Class 1–5", barClass: "" },
  { key: "upper", match: /upper primary/i, label: "Upper Primary", sub: "Class 6–8", barClass: "green" },
  { key: "secondary", match: /secondary \(9-10\)/i, label: "Secondary", sub: "Class 9–10", barClass: "orange" },
  { key: "hs", match: /higher secondary/i, label: "Higher Secondary", sub: "Class 11–12", barClass: "red" },
  { key: "pp", match: /pre-primary/i, label: "Pre-Primary", sub: "", barClass: "purple" },
];

const STAGE_COLORS = {
  primary: "#2563eb",
  upper: "#14b8a6",
  secondary: "#ff8a1f",
  hs: "#ef4444",
  pp: "#8b5cf6",
};

export const buildStageRows = (stageData = [], overview = {}) => {
  const findStage = (regex) => stageData.find((s) => regex.test(s.name || ""));
  const fallback = (key) => {
    const map = {
      primary: overview.primary_pct,
      upper: overview.upper_primary_pct,
      secondary: overview.secondary_pct,
      hs: overview.hs_pct,
      pp: overview.pp_pct,
    };
    return map[key] ?? 0;
  };

  return STAGE_ORDER.map((meta) => {
    const row = findStage(meta.match);
    const percentage = row?.percentage ?? fallback(meta.key);
    return {
      ...meta,
      percentage,
      color: STAGE_COLORS[meta.key],
    };
  });
};

export const buildStageDonutStyle = (stageRows = []) => {
  let cursor = 0;
  const stops = stageRows.map((row) => {
    const start = cursor;
    cursor += row.percentage ?? 0;
    return `${row.color} ${start}% ${cursor}%`;
  });
  if (cursor < 100 && stops.length) {
    stops.push(`${stageRows[stageRows.length - 1].color} ${cursor}% 100%`);
  }
  return {
    background: stops.length
      ? `radial-gradient(circle at center, #fff 0 52%, transparent 53%), conic-gradient(${stops.join(", ")})`
      : "radial-gradient(circle at center, #fff 0 52%, transparent 53%), conic-gradient(#e2e8f0 0 100%)",
  };
};

export const buildRetentionGaugeStyle = (retentionPct = 0) => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(#10b981 0 ${retentionPct}%, rgba(16,185,129,.14) ${retentionPct}% 100%)`,
});

export const buildEnrolmentExecutiveMarkdown = (overview = {}, blockData = []) => {
  const insights = buildExecutiveInsights(overview, [], []);
  const queue = buildActionQueue(overview);

  return [
    "## Insights",
    `- ${buildExecutiveHeroNarrative(overview)}`,
    ...insights.map((i) => `- ${i.title}: ${i.body}`),
    "",
    "## Recommendations",
    ...queue.map((a) => `- ${a.title}: ${a.body}`),
    "",
    "## Priority Action Items",
    `- ${buildPrimaryExecutiveAction(overview, blockData)}`,
    ...queue.map((a) => `- ${a.title}: ${a.body}`),
  ].join("\n");
};

export const buildExecutiveHeroNarrative = (overview = {}) => {
  const schools = fmt(overview.total_schools ?? 0);
  const enrolment = formatLakh(overview.grand_total ?? 0);
  const girls = pct(overview.girls_participation_pct ?? 0);
  const gpi = overview.gender_parity_index ?? 0;
  return `Pune district shows a strong enrolment base of ${enrolment} students across ${schools} schools. Girls share is ${girls} and the gender parity index is ${gpi}, so the executive focus should be on upper-stage transition and girls participation.`;
};

export const buildPrimaryExecutiveAction = (overview = {}, blockData = []) => {
  const gpi = asNumber(overview.gender_parity_index) ?? 1;
  const retention = asNumber(overview.secondary_hs_retention);
  const weakBlocks = [...blockData]
    .filter((block) => retentionBelow(block.secondary_retention, 85) || (asNumber(block.gpi) ?? gpi) < 0.95)
    .sort((a, b) => (asNumber(a.secondary_retention) ?? 0) - (asNumber(b.secondary_retention) ?? 0))
    .slice(0, 3)
    .map((block) => block.block_name)
    .filter(Boolean);
  const blockHint = weakBlocks.length ? ` in ${weakBlocks.join(", ")}` : "";
  const retentionText = retention != null ? `${retention.toFixed(1)}%` : "unavailable retention data";
  return `Prioritize girls retention and Class 8 → Class 11 transition checks${blockHint} where GPI is ${gpi.toFixed(2)} and secondary retention is ${retentionText}.`;
};

export const buildExecutiveInsights = (overview = {}, stageRows = [], managementData = []) => {
  const primaryShare = stageRows.find((r) => r.key === "primary")?.percentage ?? overview.primary_pct ?? 0;
  const girlsPct = overview.girls_participation_pct ?? 0;
  const gpi = overview.gender_parity_index ?? 0;
  const otherMgmt = managementData.find((m) => /other/i.test(m.management || ""));
  const otherOnly =
    otherMgmt &&
    (otherMgmt.total_enrolment ?? 0) > 0 &&
    !managementData.some(
      (m) => !/other/i.test(m.management || "") && (m.total_enrolment ?? 0) > 0
    );

  return [
    {
      id: "primary-load",
      title: "Primary Stage Carries the Load",
      body: `Primary classes account for the largest enrolment share (${pct(primaryShare)}), so resource planning should begin with Class 1–5 capacity.`,
      iconClass: "green",
    },
    {
      id: "gender-parity",
      title: "Gender Parity Needs Monitoring",
      body: `Girls share is ${pct(girlsPct)} and GPI is ${gpi}. Add block-level flags where girls participation is below district average.`,
      iconClass: "pink",
    },
    {
      id: "management-data",
      title: "Management Data Needs Validation",
      body: otherOnly
        ? 'Current records show the full share under "Other"; this should become a data quality exception, not only a chart.'
        : "Validate management master data so government, aided and unaided comparisons are reliable.",
      iconClass: "orange",
    },
  ];
};

export const buildActionQueue = (overview = {}) => {
  const girlsPct = overview.girls_participation_pct ?? 0;
  return [
    {
      priority: "Priority 1",
      title: "Girls Parity Review",
      body: `Identify blocks and classes below girls share of ${pct(girlsPct)} and create school-level outreach list.`,
      iconClass: "pink",
    },
    {
      priority: "Priority 2",
      title: "Transition Risk Check",
      body: "Track Class 8 to 9 and Class 10 to 11 transitions with retention alerts and dropout risk triggers.",
      iconClass: "green",
    },
    {
      priority: "Priority 3",
      title: "Fix Management Master Data",
      body: 'Replace "Other 100%" with validated management types to enable government vs aided vs unaided comparison.',
      iconClass: "orange",
    },
  ];
};

export const buildSizeBars = (sizeDistribution = [], maxHeight = 250) => {
  const maxCount = Math.max(...sizeDistribution.map((s) => s.count ?? 0), 1);
  const tone = (range) => {
    const r = range || "";
    if (r.startsWith("0-") || r.startsWith("51-")) return "red";
    if (r.startsWith("101-") || r.startsWith("201-")) return "orange";
    if (r.startsWith("301-") || r.startsWith("501-") || r.startsWith("801-")) return "green";
    return "";
  };
  return sizeDistribution.map((item) => ({
    ...item,
    barHeight: Math.max(18, Math.round(((item.count ?? 0) / maxCount) * maxHeight)),
    tone: tone(item.range),
  }));
};

export const buildClassChartModel = (classData = [], maxBarHeight = 224) => {
  const maxTotal = Math.max(...classData.map((c) => c.total ?? 0), 1);
  return classData.map((cls) => ({
    ...cls,
    barHeight: Math.max(16, Math.round(((cls.total ?? 0) / maxTotal) * maxBarHeight)),
  }));
};

export const buildGpiLinePath = (classData = [], width = 1000, height = 240) => {
  if (!classData.length) return { path: "", points: [] };
  const padX = 20;
  const padY = 32;
  const usableW = width - padX * 2;
  const usableH = height - padY * 2;
  const count = classData.length;
  const minG = 0.75;
  const maxG = 1.05;
  const range = maxG - minG;

  const points = classData.map((c, i) => {
    const x = padX + ((i + 0.5) / count) * usableW;
    const y = padY + ((maxG - (c.gpi ?? 0)) / range) * usableH;
    return [x, y];
  });

  const path = points
    .map(([x, y], i) => (i === 0 ? `M${x} ${y}` : `L${x} ${y}`))
    .join(" ");
  return { path, points };
};

export const buildManagementProfile = (overview = {}, managementData = []) => {
  const rows = Array.isArray(managementData) ? managementData : [];
  const withEnrolment = rows.filter((r) => (r.total_enrolment ?? 0) > 0);
  const otherOnly =
    withEnrolment.length === 1 && /other/i.test(withEnrolment[0].management || "");
  const dominant = otherOnly
    ? withEnrolment[0]
    : withEnrolment.sort((a, b) => (b.total_enrolment ?? 0) - (a.total_enrolment ?? 0))[0];

  const totalEnrol = overview.grand_total ?? 0;
  const sharePct =
    dominant && totalEnrol > 0
      ? clamp(Math.round(((dominant.total_enrolment ?? 0) / totalEnrol) * 100), 0, 100)
      : 100;

  return {
    otherOnly,
    sharePct,
    managementLabel: dominant?.management || "Other",
    schoolsClassified: fmt(dominant?.school_count ?? overview.total_schools ?? 0),
    totalEnrolment: formatLakh(overview.grand_total ?? 0),
    avgSchool: fmt(Math.round(overview.avg_school_size ?? 0)),
    headline: otherOnly
      ? 'All enrolment mapped to "Other"'
      : `Largest share: ${dominant?.management || "—"}`,
    description: otherOnly
      ? "This makes management comparison impossible. Add a data validation rule for Government, Private Aided, Private Unaided and Other before district review."
      : "Review management classification so government, aided and unaided segments are comparable at district review.",
  };
};

const CLASS_STAGE_JOURNEY = [
  {
    key: "pp",
    label: "Pre-Primary",
    sub: "PP3–PP1",
    fillClass: "pre",
    totalKey: "pp_total",
    pctKey: "pp_pct",
  },
  {
    key: "primary",
    label: "Primary",
    sub: "Class 1–5",
    fillClass: "",
    totalKey: "primary_total",
    pctKey: "primary_pct",
  },
  {
    key: "upper",
    label: "Upper Primary",
    sub: "Class 6–8",
    fillClass: "upper",
    totalKey: "upper_primary_total",
    pctKey: "upper_primary_pct",
  },
  {
    key: "secondary",
    label: "Secondary",
    sub: "Class 9–10",
    fillClass: "secondary",
    totalKey: "secondary_total",
    pctKey: "secondary_pct",
  },
  {
    key: "hs",
    label: "Higher Sec.",
    sub: "Class 11–12",
    fillClass: "high",
    totalKey: "hs_total",
    pctKey: "hs_pct",
  },
];

export const findPeakClass = (classData = []) => {
  if (!classData.length) return null;
  return classData.reduce((best, row) =>
    (row.total ?? 0) > (best.total ?? 0) ? row : best
  );
};

export const buildClassStageJourneyRows = (overview = {}) => {
  const primaryTotal = overview.primary_total ?? 1;
  return CLASS_STAGE_JOURNEY.map((meta) => {
    const total = overview[meta.totalKey] ?? 0;
    const sharePct = overview[meta.pctKey] ?? 0;
    const barWidth =
      meta.key === "primary"
        ? 100
        : clamp(Math.round((total / primaryTotal) * 1000) / 10, 4, 100);
    return { ...meta, total, sharePct, barWidth };
  });
};

export const buildStageKpiCards = (overview = {}) => [
  {
    tone: "purple",
    label: "Pre-Primary",
    total: overview.pp_total ?? 0,
    caption: `${pct(overview.pp_pct ?? 0)} share · early learning base`,
  },
  {
    tone: "",
    label: "Primary · 1–5",
    total: overview.primary_total ?? 0,
    caption: `${pct(overview.primary_pct ?? 0)} share · highest load`,
  },
  {
    tone: "green",
    label: "Upper Primary · 6–8",
    total: overview.upper_primary_total ?? 0,
    caption: `${pct(overview.upper_primary_pct ?? 0)} share · steady transition`,
  },
  {
    tone: "orange",
    label: "Secondary · 9–10",
    total: overview.secondary_total ?? 0,
    caption: `${pct(overview.secondary_pct ?? 0)} share · retention watch`,
  },
  {
    tone: "red",
    label: "Higher Sec. · 11–12",
    total: overview.hs_total ?? 0,
    caption: `${pct(overview.hs_pct ?? 0)} share · capacity focus`,
  },
];

const GENDER_CHART_CLASSES = ["PP3", "PP2", "Class 1", "Class 5", "Class 8", "Class 9", "Class 11", "Class 12"];

export const buildGenderChartRows = (classData = []) => {
  const byName = Object.fromEntries(classData.map((c) => [c.class_name, c]));
  return GENDER_CHART_CLASSES.map((name) => {
    const row = byName[name];
    if (!row) return null;
    const total = row.total ?? 1;
    const boysPct = clamp(((row.boys ?? 0) / total) * 100, 0, 100);
    const girlsPct = clamp(100 - boysPct, 0, 100);
    const gpi = row.gpi ?? 0;
    return {
      class_name: name,
      boysPct,
      girlsPct,
      gpi,
      gpiGood: gpi >= 0.95,
    };
  }).filter(Boolean);
};

export const buildTransitionFlowSteps = (overview = {}) => {
  const primaryTotal = overview.primary_total ?? 1;
  return CLASS_STAGE_JOURNEY.map((meta) => {
    const total = overview[meta.totalKey] ?? 0;
    const relative =
      meta.key === "primary" ? 100 : clamp(Math.round((total / primaryTotal) * 100), 1, 100);
    const captions = {
      pp: "Early learning base compared with primary",
      primary: "Largest enrolment and capacity anchor",
      upper: "Healthy but needs school-wise tracking",
      secondary: "Retention pressure begins here",
      hs: "Subject, stream and capacity focus",
    };
    const barTone =
      meta.key === "secondary" ? "secondary" : meta.key === "hs" ? "high" : "";
    return {
      ...meta,
      relative,
      caption: captions[meta.key] || "",
      barTone,
    };
  });
};

export const deriveClassStatus = (cls = {}, peakClassName = "") => {
  const gpi = cls.gpi ?? 0;
  const girlsPct = cls.girls_pct ?? 0;
  const name = cls.class_name || "";

  if (name === peakClassName) return "Capacity focus";
  if (/^PP/i.test(name) && gpi >= 0.93) return "Balanced";
  if (gpi >= 0.98 || (gpi >= 0.95 && girlsPct >= 48.5)) return "Balanced";
  if (gpi < 0.87 || girlsPct < 46) return "Parity risk";
  if (/Class 10/i.test(name)) return "Transition watch";
  if (/Class 11/i.test(name)) return "Retention focus";
  return "Watch";
};

export const statusTagClass = (status = "") => {
  if (status === "Balanced") return "good";
  if (status === "Parity risk") return "risk";
  return "";
};

export const buildClassStageHeroCopy = (overview = {}, peakClass = null) => {
  const stages = [
    { label: "Pre-Primary", pct: overview.pp_pct ?? 0 },
    { label: "Primary", pct: overview.primary_pct ?? 0 },
    { label: "Upper Primary", pct: overview.upper_primary_pct ?? 0 },
    { label: "Secondary", pct: overview.secondary_pct ?? 0 },
    { label: "Higher Secondary", pct: overview.hs_pct ?? 0 },
  ].sort((a, b) => b.pct - a.pct);
  const largest = stages[0] || { label: "Primary", pct: overview.primary_pct ?? 0 };

  return {
  title: `${largest.label} Stage Carries the Largest Enrolment Load.`,
  body: `${pct(largest.pct)} of students are in ${largest.label.toLowerCase()} classes, with participation tracked through Class 10 and tapering in Classes 11–12. The tab separates executive insight, stage mix, gender balance and detailed class table.`,
  largestStage: pct(largest.pct),
  peakClass: peakClass ? formatCompactK(peakClass.total) : "—",
  peakClassLabel: peakClass?.class_name || "Peak class",
  girlsShare: pct(overview.girls_participation_pct ?? 0),
  primaryAction:
    "Track the transition from Class 10 to Class 11 and compare higher-secondary capacity, subject availability and retention support at block level.",
  };
};

export const buildClassStageActions = (overview = {}, classData = []) => {
  const girlsPct = overview.girls_participation_pct ?? 0;
  const peak = findPeakClass(classData);
  return [
    {
      iconClass: "red",
      title: "Investigate Class 10 → 11 Drop",
      body: "Compare block-level capacity, subject availability, transport and scholarship coverage for higher-secondary continuation.",
    },
    {
      iconClass: "orange",
      title: "Flag Classes 8–9 for Parity Review",
      body: `Girls share is comparatively lower in Classes 8–9; add block-level gender parity heatmap before review meetings.`,
    },
    {
      iconClass: "green",
      title: peak ? `Use ${peak.class_name} as capacity benchmark` : "Use peak class as capacity benchmark",
      body: peak
        ? `${peak.class_name} has the highest enrolment; use it for teacher allocation, classroom pressure and transition planning.`
        : "Use the peak enrolment class for teacher allocation, classroom pressure and transition planning.",
    },
  ];
};

export const buildClassStageDonutList = (overview = {}) =>
  CLASS_STAGE_JOURNEY.map((meta) => ({
    label: meta.label === "Higher Sec." ? "Higher Secondary" : meta.label,
    pct: overview[meta.pctKey] ?? 0,
    color: STAGE_COLORS[meta.key === "hs" ? "hs" : meta.key],
  }));

const GPI_TARGET_MIN = 0.95;
const GPI_TARGET_MAX = 1.05;

export const deriveParityBand = (gpi = 0) => {
  if (gpi >= GPI_TARGET_MIN) {
    return { band: "good", heatLabel: "Within target", tableLabel: "Within Target" };
  }
  if (gpi >= 0.93) {
    return { band: "watch", heatLabel: "Near target", tableLabel: "Near Target" };
  }
  if (gpi <= 0.87) {
    return { band: "risk", heatLabel: "Priority risk", tableLabel: "Priority Risk" };
  }
  return { band: "risk", heatLabel: "Below target", tableLabel: "Below Target" };
};

export const buildGpiGaugeStyle = (gpi = 0) => {
  const fill = clamp(Math.round(gpi * 100), 0, 100);
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(#f59e0b 0 ${fill}%, rgba(245,158,11,.14) ${fill}% 100%)`,
  };
};

export const findHighestGapClass = (classData = []) => {
  if (!classData.length) return null;
  return classData.reduce((best, row) => {
    const gap = (row.boys ?? 0) - (row.girls ?? 0);
    const bestGap = (best.boys ?? 0) - (best.girls ?? 0);
    return gap > bestGap ? row : best;
  });
};

export const findLowestGpiClass = (classData = []) => {
  if (!classData.length) return null;
  return classData.reduce((best, row) =>
    (row.gpi ?? 1) < (best.gpi ?? 1) ? row : best
  );
};

const formatShortClass = (name = "") => {
  if (/^PP\d$/i.test(name)) return name.replace(/^PP/i, "PP");
  if (/^Class (\d+)$/i.test(name)) return `C${name.match(/\d+/)[0]}`;
  return name;
};

export const buildGpiTrendChart = (classData = []) => {
  const width = 720;
  const padLeft = 64;
  const padRight = 42;
  const top = 64;
  const bottom = 268;
  const minG = 0.75;
  const maxG = 1.05;
  const usableW = width - padLeft - padRight;
  const step = classData.length > 1 ? usableW / (classData.length - 1) : 0;

  const yForGpi = (gpi) => top + ((maxG - (gpi ?? 0)) / (maxG - minG)) * (bottom - top);

  const points = classData.map((row, i) => ({
    x: padLeft + i * step,
    y: yForGpi(row.gpi),
    gpi: row.gpi ?? 0,
    class_name: row.class_name,
    shortLabel: formatShortClass(row.class_name),
    isRisk: (row.gpi ?? 0) < 0.87,
  }));

  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x} ${p.y}`).join(" ");
  const lowest = findLowestGpiClass(classData);
  const lowestPoint = points.find((p) => p.class_name === lowest?.class_name);

  return { path, points, lowest, lowestPoint };
};

export const buildGenderGapRows = (classData = [], limit = 7) => {
  const rows = classData
    .map((row) => ({
      ...row,
      gap: Math.max(0, (row.boys ?? 0) - (row.girls ?? 0)),
    }))
    .sort((a, b) => b.gap - a.gap)
    .slice(0, limit);
  const maxGap = Math.max(...rows.map((r) => r.gap), 1);
  return rows.map((row) => ({
    ...row,
    barWidth: Math.max(16, Math.round((row.gap / maxGap) * 100)),
    tone: row.gap >= 13000 ? "danger" : row.gap >= 9500 ? "risk" : "",
  }));
};

export const buildGenderComposition = (overview = {}) => {
  const total = overview.grand_total ?? 1;
  const boys = overview.total_boys ?? 0;
  const girls = overview.total_girls ?? 0;
  const trans = overview.total_trans ?? 0;
  const boysPct = clamp((boys / total) * 100, 0, 100);
  const girlsPct = clamp((girls / total) * 100, 0, 100);
  const transPct = trans > 0 ? Math.max((trans / total) * 100, 0.2) : 0;
  const girlsEnd = boysPct + girlsPct;
  const stops = [`#2563eb 0 ${boysPct}%`, `#ec4899 ${boysPct}% ${girlsEnd}%`];
  if (trans > 0) stops.push(`#8b5cf6 ${girlsEnd}% 100%`);
  else stops.push(`#ec4899 ${girlsEnd}% 100%`);

  return {
    donutStyle: { background: `conic-gradient(${stops.join(", ")})` },
    boys,
    girls,
    trans,
    boysPct,
    girlsPct,
    transLabel: trans > 0 ? `${fmt(trans)} records` : "0 records",
    transBarWidth: trans > 0 ? Math.max(transPct, 0.2) : 0,
    girlsShare: overview.girls_participation_pct ?? 0,
  };
};

const STAGE_PARITY_META = [
  { key: "pp", match: /pre-primary/i, label: "Pre-Primary" },
  { key: "primary", match: /primary \(1-5\)/i, label: "Primary 1–5" },
  { key: "upper", match: /upper primary/i, label: "Upper Primary 6–8" },
  { key: "secondary", match: /secondary \(9-10\)/i, label: "Secondary 9–10" },
  { key: "hs", match: /higher secondary/i, label: "Higher Sec 11–12" },
];

export const buildStageParityCards = (stageData = []) => {
  const labels = {
    upper: "Priority watch",
    secondary: "Transition risk",
  };
  return STAGE_PARITY_META.map((meta) => {
    const row = stageData.find((s) => meta.match.test(s.name || ""));
    const gpi = row?.gpi ?? 0;
    let tone = "risk";
    let status = "Below target";
    if (gpi >= GPI_TARGET_MIN) {
      tone = "good";
      status = "Within target";
    } else if (gpi >= 0.93) {
      tone = "watch";
      status = "Near target";
    } else if (labels[meta.key]) {
      status = labels[meta.key];
    }
    return { ...meta, gpi, tone, status, meterWidth: clamp(Math.round(gpi * 100), 0, 100) };
  });
};

export const buildGenderInclusionInsights = (overview = {}, classData = []) => {
  const highestGap = findHighestGapClass(classData);
  const gap = highestGap ? Math.max(0, (highestGap.boys ?? 0) - (highestGap.girls ?? 0)) : 0;
  return [
    {
      id: "parity",
      title: "District Parity",
      body: "GPI is below target, requiring focused girl enrolment and retention monitoring.",
      stat: overview.gender_parity_index ?? 0,
      statSuffix: "GPI",
      iconClass: "",
    },
    {
      id: "girls-share",
      title: "Girls Share",
      body: "Girls are close to half of enrolment, but not yet at a balanced distribution.",
      stat: pct(overview.girls_participation_pct ?? 0),
      statSuffix: "girls",
      iconClass: "pink",
    },
    {
      id: "gap-class",
      title: "Highest Gap Class",
      body: highestGap
        ? `${highestGap.class_name} has the largest absolute gap and should be reviewed first.`
        : "Review classes with the largest absolute gender gap first.",
      stat: fmt(gap),
      statSuffix: "gap",
      iconClass: "orange",
    },
    {
      id: "inclusion",
      title: "Inclusion Records",
      body: "Transgender enrolment is present but extremely low and should be handled carefully.",
      stat: fmt(overview.total_trans ?? 0),
      statSuffix: "records",
      iconClass: "purple",
    },
  ];
};

export const buildGenderHeroCopy = (overview = {}, classData = []) => {
  const gpi = overview.gender_parity_index ?? 0;
  const girlsPct = overview.girls_participation_pct ?? 0;
  const gap = fmt(overview.gender_gap ?? 0);
  const lowest = findLowestGpiClass(classData);
  const watchClasses = classData
    .filter((c) => (c.gpi ?? 1) <= 0.87)
    .map((c) => c.class_name)
    .slice(0, 2)
    .join(" and ");

  return {
    title: gpi >= GPI_TARGET_MIN ? "Parity is within target band" : "Parity is close, but below target",
    body: `Girls represent ${pct(girlsPct)} of enrolment. The district has a gap of ${gap} students${
      watchClasses ? `, with ${watchClasses} contributing the highest difference between boys and girls.` : "."
    }`,
    primaryAction:
      "Prioritize upper-primary and secondary transition campaigns where gender gap widens before Class 10 and Class 11.",
    belowTarget: gpi < GPI_TARGET_MIN,
  };
};

export const buildGenderActionQueue = (overview = {}) => [
  {
    iconClass: "orange",
    title: "Focus Class 8 and Class 9",
    body: "These classes show the largest absolute gender gap and lowest parity levels.",
  },
  {
    iconClass: "pink",
    title: "Improve girls' transition tracking",
    body: "Monitor continuation from upper-primary to secondary with school-wise exception lists.",
  },
  {
    iconClass: "purple",
    title: "Strengthen Inclusion Data Quality",
    body: "Verify low transgender records sensitively and ensure privacy-preserving reporting.",
  },
];

export const buildTransitionDonutStyle = (score = 0, color = "#10b981") => ({
  "--score": clamp(Number(score) || 0, 0, 100),
  "--color": color,
});

export const deriveTransitionStatus = (value = 0, { criticalBelow = 85, excellentAt = 95 } = {}) => {
  if (value >= excellentAt) {
    return { pill: "good", label: "Excellent Performance", color: "#10b981", critical: false };
  }
  if (value >= 90) {
    return { pill: "good", label: "Good Performance", color: "#10b981", critical: false };
  }
  if (value >= criticalBelow) {
    return { pill: "watch", label: "Needs Attention", color: "#f59e0b", critical: false };
  }
  return { pill: "bad", label: "High Risk", color: "#ef4444", critical: true };
};

export const buildTransitionCards = (overview = {}) => [
  {
    key: "primary-upper",
    title: "Primary → Upper Primary",
    subtitle: "Transition Retention",
    value: overview.primary_upper_retention ?? 0,
    footnote: "Class 5 → Class 6",
    iconClass: "green",
  },
  {
    key: "upper-secondary",
    title: "Upper Primary → Secondary",
    subtitle: "Transition Retention",
    value: overview.upper_secondary_retention ?? 0,
    footnote: "Class 8 → Class 9",
    iconClass: "green",
    excellentAt: 95,
  },
  {
    key: "secondary-hs",
    title: "Secondary → Higher Sec",
    subtitle: "Transition Retention",
    value: overview.secondary_hs_retention ?? 0,
    footnote: "Class 10 → Class 11 (Critical)",
    iconClass: "red",
    criticalBelow: 85,
  },
].map((card) => ({
  ...card,
  status: deriveTransitionStatus(card.value, {
    criticalBelow: card.criticalBelow ?? 85,
    excellentAt: card.excellentAt ?? 95,
  }),
}));

export const buildRetentionRiskKpis = (overview = {}, blockData = [], sizeDistribution = []) => {
  const totalSchools = overview.total_schools ?? 1;
  const smallCount = sizeDistribution
    .filter((row) => ["0-50", "51-100"].includes(row.range))
    .reduce((sum, row) => sum + (row.count || 0), 0);
  const largeCount = sizeDistribution
    .filter((row) => ["1001-1500", "1500+"].includes(row.range))
    .reduce((sum, row) => sum + (row.count || 0), 0);
  const lowBlocks = blockData.filter((block) => retentionBelow(block.secondary_retention, 85));
  const interventionSchools = lowBlocks.reduce((sum, block) => sum + (block.total_schools ?? 0), 0);

  return {
    atRiskSchools: smallCount,
    atRiskPct: pct((smallCount / totalSchools) * 100, 1),
    lowRetentionBlocks: lowBlocks.length,
    schoolsNeedingIntervention: interventionSchools,
    smallCount,
    largeCount,
  };
};

export const buildRetentionComboChart = (retentionData = [], overview = {}) => {
  const ppEnrolment =
    overview.pp_total ??
    retentionData
      .filter((row) => /^PP/i.test(row.class_name))
      .reduce((sum, row) => sum + (row.enrolment || 0), 0);
  const classRows = retentionData.filter((row) => !/^PP/i.test(row.class_name));

  const rows = [
    { label: "Pre-Primary", shortLabel: "Pre-Primary", enrolment: ppEnrolment, drop_pct: 0, isRisk: false },
    ...classRows.map((row) => ({
      label: row.class_name,
      shortLabel: row.class_name,
      enrolment: row.enrolment || 0,
      drop_pct: row.drop_pct || 0,
      isRisk: row.is_critical || (row.drop_pct || 0) > 15,
    })),
  ];

  const peakEnrolment = Math.max(...rows.map((r) => r.enrolment), 1);
  const enrolMax = Math.ceil(peakEnrolment / 55000) * 55000 || peakEnrolment;
  const CHART_H = 264;
  const BASE_Y = 280;
  const STEP = 65;
  const BAR_W = 42;

  const points = rows.map((row, index) => {
    const x = 20 + index * STEP;
    const barH = Math.max(4, Math.round((row.enrolment / enrolMax) * CHART_H));
    const barY = BASE_Y - barH;
    const lineY = BASE_Y - Math.max(0, Math.min(CHART_H, ((row.drop_pct || 0) / 20) * CHART_H));
    return { ...row, x, barH, barY, lineY, cx: x + BAR_W / 2 };
  });

  const peak = [...points].sort((a, b) => (b.drop_pct || 0) - (a.drop_pct || 0))[0];
  const peakIndex = points.findIndex((point) => point.label === peak?.label);
  const riskZone =
    peakIndex >= 0 && (peak?.drop_pct || 0) > 10
      ? { x: Math.max(12, points[Math.max(0, peakIndex - 1)].x - 8), width: STEP * 2 + 16 }
      : null;

  return {
    points,
    enrolMax,
    riskZone,
    linePath: points.map((point, index) => `${index === 0 ? "M" : "L"}${point.cx} ${point.lineY}`).join(" "),
    peak,
  };
};

export const deriveBlockRiskStatus = (retention = 0) => {
  if (retention >= 90) return { label: "Low Risk", tag: "green" };
  if (retention >= 80) return { label: "Medium Risk", tag: "amber" };
  return { label: "High Risk", tag: "red" };
};

export const buildRiskHotspots = (overview = {}, blockData = [], classData = [], kpis = {}) => {
  const c10 = classData.find((row) => row.class_name === "Class 10");
  const c11 = classData.find((row) => row.class_name === "Class 11");

  return [
    {
      rank: 1,
      rankClass: "red",
      title: "Critical: Class 10 → 11 Transition",
      body: `Highest dropout spike at higher secondary entry (${pct(overview.secondary_hs_retention ?? 0, 1)} retention)`,
      tag: "red",
      tagLabel: "High",
    },
    {
      rank: 2,
      rankClass: "orange",
      title: "Low Enrolment Schools",
      body: `${fmt(kpis.smallCount ?? 0)} schools have fewer than 100 students`,
      tag: "amber",
      tagLabel: "Medium",
    },
    {
      rank: 3,
      rankClass: "orange",
      title: "Blocks Below Target Retention",
      body: `${kpis.lowRetentionBlocks ?? 0} blocks are below the 85% secondary retention target`,
      tag: "amber",
      tagLabel: "Medium",
    },
    {
      rank: 4,
      rankClass: "orange",
      title: "Higher Secondary Capacity Pressure",
      body: c10 && c11
        ? `Class 11 enrolment is ${fmt(c11.total ?? c11.enrolment ?? 0)} against ${fmt(c10.total ?? c10.enrolment ?? 0)} in Class 10`
        : "Limited seats versus demand in Class 11",
      tag: "amber",
      tagLabel: "Medium",
    },
    {
      rank: 5,
      rankClass: "pink",
      title: "Girls' Retention (Sec → HS)",
      body:
        (overview.gender_parity_index ?? 1) < 0.95
          ? `Gender gap widens with GPI at ${overview.gender_parity_index ?? 0}`
          : "Monitor girls continuation from secondary to higher secondary",
      tag: "pink",
      tagLabel: "Watch",
    },
  ];
};

export const buildRetentionAlertCopy = (overview = {}) => ({
  title: "Critical Transition Risk at Class 10 → 11.",
  body: "Focus retention interventions in higher secondary entry cohorts.",
  show: retentionBelow(overview.secondary_hs_retention, 90),
});

export const getWorstRetentionBlocks = (blockData = [], limit = 3) =>
  [...blockData]
    .filter((block) => asNumber(block.secondary_retention) != null)
    .sort((a, b) => asNumber(a.secondary_retention) - asNumber(b.secondary_retention))
    .slice(0, limit);

const deriveSignalLevel = (value, { highBelow, watchBelow }) => {
  if (value < highBelow) return { label: "High", tone: "red", width: clamp(95 - value * 0.4, 55, 95) };
  if (value < watchBelow) return { label: "Watch", tone: "orange", width: clamp(80 - value * 0.25, 45, 78) };
  if (value < watchBelow + 8) return { label: "Medium", tone: "", width: clamp(65 - value * 0.15, 35, 65) };
  return { label: "Low", tone: "green", width: clamp(50 - value * 0.1, 18, 42) };
};

export const countEnrolmentPrioritySignals = (overview = {}, blockData = [], sizeDistribution = []) => {
  let critical = 0;
  let watch = 0;
  if (retentionBelow(overview.secondary_hs_retention, 85)) critical += 1;
  else if (retentionBelow(overview.secondary_hs_retention, 90)) watch += 1;
  if ((overview.gender_parity_index ?? 1) < 0.9) critical += 1;
  else if ((overview.gender_parity_index ?? 1) < 0.95) watch += 1;
  const lowBlocks = blockData.filter((block) => retentionBelow(block.secondary_retention, 85));
  if (lowBlocks.length >= 2) critical += 1;
  else if (lowBlocks.length > 0) watch += 1;
  const kpis = buildRetentionRiskKpis(overview, blockData, sizeDistribution);
  if (kpis.smallCount > 0) watch += 1;
  if (kpis.largeCount > 0) watch += 1;
  if (typeof overview.data_completeness_pct === "number" && overview.data_completeness_pct < 90) watch += 1;
  return { critical, watch, total: critical + watch };
};

export const buildGenderInsightsMarkdown = (overview = {}, classData = []) => {
  const hero = buildGenderHeroCopy(overview, classData);
  const insights = buildGenderInclusionInsights(overview, classData);
  const actions = buildGenderActionQueue(overview);

  return [
    "## Insights",
    `- ${hero.title}: ${hero.body}`,
    ...insights.map((item) => `- **${item.title}**: ${item.body}`),
    "",
    "## Root Cause Signals",
    "- Class-level GPI gaps widen in upper-primary and secondary transitions.",
    "- Girls participation below target in selected classes drives district parity gap.",
    "- Inclusion records need privacy-preserving validation.",
    "",
    "## Recommendations",
    ...actions.map((item) => `- **${item.title}**: ${item.body}`),
    "",
    "## Priority Action Items",
    `- ${hero.primaryAction}`,
    "- Assign block owners for class-wise parity closure within 14 days.",
    "- Review girls transition tracking from Class 8 to Class 11.",
  ].join("\n");
};

export const buildRetentionInsightsMarkdown = (
  overview = {},
  blockData = [],
  retentionData = [],
  classData = []
) => {
  const alert = buildRetentionAlertCopy(overview);
  const transitions = buildTransitionCards(overview);
  const hotspots = buildRiskHotspots(
    overview,
    blockData,
    classData,
    buildRetentionRiskKpis(overview, blockData, [])
  );
  const worstBlocks = getWorstRetentionBlocks(blockData, 3);

  return [
    "## Insights",
    alert.show
      ? `- **${alert.title}** ${alert.body}`
      : "- Transition retention is within acceptable band; continue monitoring.",
    ...transitions.map(
      (card) => `- **${card.title}**: ${pct(card.value, 1)} (${card.footnote})`
    ),
    worstBlocks.length
      ? `- Lowest retention blocks: **${worstBlocks.map((b) => b.block_name).join(", ")}**.`
      : "- Block retention data unavailable for current scope.",
    "",
    "## Root Cause Signals",
    ...hotspots.slice(0, 4).map((h) => `- **${h.title}**: ${h.body}`),
    retentionData.length
      ? `- Class-wise retention series available across **${retentionData.length}** transition points.`
      : "- Class-wise retention series pending.",
    "",
    "## Recommendations",
    "- Launch Class 10 → 11 continuation sprint in lowest-retention blocks.",
    "- Validate attendance and transfer records before escalating dropout cases.",
    "- Separate intervention playbooks for small vs large schools.",
    "",
    "## Priority Action Items",
    worstBlocks.length
      ? `- Week 1: focus retention support in **${worstBlocks.map((b) => b.block_name).join(", ")}**.`
      : "- Week 1: identify lowest-retention blocks and assign owners.",
    "- Week 2: run school-wise transition counselling and seat planning.",
    "- Week 3–4: re-measure retention and close block action items.",
  ].join("\n");
};

export const buildEnrolmentInsightsMarkdown = (overview = {}, blockData = [], sizeDistribution = []) => {
  if (!overview?.total_schools) {
    return [
      "## Insights",
      "- No enrolment data available for the current filters.",
      "",
      "## Root Cause Signals",
      "- Missing or incomplete records in the selected scope.",
      "",
      "## Recommendations",
      "- Refresh data or broaden filters to include valid records.",
      "",
      "## Priority Action Items",
      "- Validate data import for enrolment analytics.",
    ].join("\n");
  }

  const worstRetention = getWorstRetentionBlocks(blockData, 3);
  const retentionIndex =
    overview.secondary_retention_index ?? overview.secondary_hs_retention ?? "unavailable";

  return [
    "## Insights",
    `- Total enrolment: **${fmt(overview.grand_total)}** across **${fmt(overview.total_schools)}** schools.`,
    `- Girls participation: **${pct(overview.girls_participation_pct)}** with GPI **${overview.gender_parity_index ?? "—"}**.`,
    `- Secondary retention index: **${retentionIndex}**.`,
    worstRetention.length
      ? `- Lowest retention blocks: **${worstRetention.map((b) => b.block_name).join(", ")}**.`
      : "- Lowest retention blocks: unavailable.",
    "",
    "## Root Cause Signals",
    "- Lower secondary retention indicates transition gaps from Class 10 to Class 11.",
    "- GPI below parity in select blocks suggests gender participation barriers.",
    "- Small and large school cohorts need differentiated intervention playbooks.",
    "",
    "## Recommendations",
    "- Focus retention drives in low-retention blocks with school-level tracking.",
    "- Strengthen transition support (counselling, transport, scholarships).",
    "- Create class-wise girls participation and retention heatmaps.",
    "",
    "## Priority Action Items",
    worstRetention.length
      ? `- Week 1: boost retention in **${worstRetention.map((b) => b.block_name).join(", ")}** where secondary retention is lowest.`
      : "- Week 1: boost retention in lowest retention blocks.",
    `- Week 2: lift girls participation (**${pct(overview.girls_participation_pct)}**) via targeted enrolment drives.`,
    "- Week 3–4: review transition drop-off and update block action plans.",
  ].join("\n");
};

export const buildEnrolmentHeroMetrics = (overview = {}, blockData = [], sizeDistribution = []) => {
  const blocks = getWorstRetentionBlocks(blockData, 3);
  const counts = countEnrolmentPrioritySignals(overview, blockData, sizeDistribution);
  return {
    totalEnrolment: formatLakh(overview.grand_total ?? 0),
    schoolsLabel: `Across ${fmt(overview.total_schools ?? 0)} schools`,
    girlsPct: pct(overview.girls_participation_pct ?? 0, 1),
    gpiLabel: `GPI ${overview.gender_parity_index ?? "—"}`,
    retentionWatchCount: blocks.length || 0,
    retentionWatchBlocks: blocks.map((b) => b.block_name).join(", ") || "—",
    priorityActions: counts.total,
    priorityActionsLabel: `${counts.critical} critical · ${counts.watch} watch`,
  };
};

export const buildEnrolmentPriorityAlert = (overview = {}, blockData = []) => {
  const blocks = getWorstRetentionBlocks(blockData, 3);
  const blockNames = blocks.map((b) => b.block_name).join(", ");
  return {
    title: "Priority Insight: Secondary Retention Needs Immediate Attention",
    body: blocks.length
      ? `Lowest retention signals are concentrated in ${blockNames}. Targeted intervention should focus on Class 10 → 11 transition planning and girls participation in higher secondary.`
      : "Secondary retention signals require review. Target intervention on Class 10 → 11 transition and girls participation in higher secondary.",
    show: retentionBelow(overview.secondary_hs_retention, 92) || blocks.length > 0,
  };
};

export const buildEnrolmentExecutiveCards = (overview = {}, blockData = [], sizeDistribution = []) => {
  const blocks = getWorstRetentionBlocks(blockData, 3);
  const kpis = buildRetentionRiskKpis(overview, blockData, sizeDistribution);
  const gpi = overview.gender_parity_index ?? 0;
  const cohorts =
    (kpis.smallCount > 0 ? 1 : 0) + (kpis.largeCount > 0 ? 1 : 0) || (sizeDistribution.length ? 2 : 0);

  return [
    {
      id: "enrolment-base",
      title: "Enrolment Base Is Broad",
      body: `${formatLakh(overview.grand_total ?? 0).replace("L", " lakh")} students across ${fmt(overview.total_schools ?? 0)} schools gives strong coverage, but size distribution needs differentiated monitoring.`,
      metric: formatLakh(overview.grand_total ?? 0),
      metricUnit: "students",
      badge: "Stable",
      badgeClass: "green",
      iconClass: "",
    },
    {
      id: "gender-parity",
      title: "Gender Parity Is Below Target",
      body: `Girls participation is ${pct(overview.girls_participation_pct ?? 0, 1)} and GPI is ${gpi}, indicating a targeted inclusion focus is needed in selected classes and blocks.`,
      metric: String(gpi || "—"),
      metricUnit: "GPI",
      badge: gpi < 0.95 ? "Watch" : "Stable",
      badgeClass: gpi < 0.95 ? "amber" : "green",
      iconClass: "purple",
    },
    {
      id: "retention-risk",
      title: "Retention Risk Is Clustered",
      body: "Lowest retention blocks are concentrated enough for a focused sprint rather than district-wide generic intervention.",
      metric: String(blocks.length || 0),
      metricUnit: "priority blocks",
      badge: blocks.length >= 2 ? "Critical" : blocks.length ? "Watch" : "Stable",
      badgeClass: blocks.length >= 2 ? "red" : blocks.length ? "amber" : "green",
      iconClass: "orange",
    },
    {
      id: "school-size",
      title: "School-Size Risk Is Split",
      body: "Small schools require viability support, while large schools require capacity and transition seat planning.",
      metric: String(cohorts || 2),
      metricUnit: "school cohorts",
      badge: "Segment",
      badgeClass: "",
      iconClass: "green",
    },
  ];
};

export const buildEnrolmentRootCauseSignals = (overview = {}, blockData = [], sizeDistribution = []) => {
  const secondaryRet = asNumber(overview.secondary_hs_retention);
  const gpiGap = Math.max(0, 1 - (overview.gender_parity_index ?? 1)) * 100;
  const kpis = buildRetentionRiskKpis(overview, blockData, sizeDistribution);
  const smallShare = ((kpis.smallCount ?? 0) / Math.max(overview.total_schools ?? 1, 1)) * 100;
  const largeShare = ((kpis.largeCount ?? 0) / Math.max(overview.total_schools ?? 1, 1)) * 100;
  const dataGap = Math.max(0, 100 - (overview.data_completeness_pct ?? 0));

  const retentionSignal =
    secondaryRet != null
      ? deriveSignalLevel(secondaryRet, { highBelow: 85, watchBelow: 90 })
      : { label: "No Data", tone: "", width: 20 };
  const paritySignal = deriveSignalLevel(100 - gpiGap, { highBelow: 92, watchBelow: 95 });
  const smallSignal = deriveSignalLevel(100 - smallShare * 1.2, { highBelow: 70, watchBelow: 82 });
  const largeSignal = deriveSignalLevel(100 - largeShare * 2, { highBelow: 75, watchBelow: 88 });
  const dataSignal = deriveSignalLevel(100 - dataGap, { highBelow: 80, watchBelow: 92 });

  const toRow = (name, signal) => ({
    label: name,
    value: signal.label,
    tone: signal.tone,
    width: signal.width,
  });

  return [
    toRow("Class 10 → 11 retention", retentionSignal),
    toRow("Girls participation gap", paritySignal),
    toRow("Small school viability", smallSignal),
    toRow("Large school capacity", largeSignal),
    toRow("Data completeness", dataSignal),
  ];
};

const deriveInterventionHorizon = (overview = {}, urgency = "high") => {
  const retention = asNumber(overview.secondary_hs_retention);
  if (urgency === "high") {
    if (retention == null) return "Review when data loads";
    return retention < 85 ? "7 days" : retention < 90 ? "14 days" : "21 days";
  }
  if (urgency === "medium") return (overview.gender_parity_index ?? 1) < 0.9 ? "14 days" : "21 days";
  if (retention == null) return "30 days";
  return retention < 88 ? "21 days" : "30 days";
};

export const buildEnrolmentRecommendations = (overview = {}, blockData = []) => {
  const blocks = getWorstRetentionBlocks(blockData, 3);
  const blockHint = blocks.length ? ` in ${blocks.map((b) => b.block_name).join(", ")}` : "";
  return [
    {
      num: "1",
      tone: "red",
      title: "Run a Class 10 → 11 Transition Campaign",
      body: `Identify students at risk before secondary completion${blockHint} and assign school-level follow-up.`,
      badge: deriveInterventionHorizon(overview, "high"),
      badgeClass: "red",
    },
    {
      num: "2",
      tone: "amber",
      title: "Create Girls Retention Heatmap",
      body: "Map parity and retention together for classes where girls participation weakens.",
      badge: deriveInterventionHorizon(overview, "medium"),
      badgeClass: "amber",
    },
    {
      num: "3",
      tone: "",
      title: "Separate School-Size Playbooks",
      body: "Use different interventions for small viability schools and high-capacity schools.",
      badge: deriveInterventionHorizon(overview, "low"),
      badgeClass: "",
    },
  ];
};

export const buildEnrolmentBlockRiskIntensity = (blockData = []) =>
  [...blockData]
    .map((block) => {
      const retention = block.secondary_retention ?? 0;
      const status = deriveBlockRiskStatus(retention);
      const intensity = clamp(100 - retention, 18, 92);
      const label =
        status.tag === "red" ? "High" : status.tag === "amber" ? "Watch" : "Monitor";
      const tone = status.tag === "red" ? "red" : status.tag === "amber" ? "orange" : "";
      return { name: block.block_name, width: intensity, label, tone, retention };
    })
    .sort((a, b) => a.retention - b.retention)
    .slice(0, 4);

export const buildEnrolmentPriorityItems = (overview = {}) => {
  const secondaryCritical = retentionBelow(overview.secondary_hs_retention, 88);
  const parityWatch = (overview.gender_parity_index ?? 1) < 0.95;
  return [
    {
      rank: "P1",
      tone: "red",
      title: "Approve Transition Intervention Sprint",
      body: "Focus on low-retention blocks and Class 10 → 11 continuation.",
      badge: secondaryCritical ? "Critical" : "Watch",
      badgeClass: secondaryCritical ? "red" : "amber",
    },
    {
      rank: "P2",
      tone: "amber",
      title: "Validate Girls Participation Gap",
      body: "Check class-level parity and school-level dropout signals before action allocation.",
      badge: parityWatch ? "Watch" : "Routine",
      badgeClass: parityWatch ? "amber" : "green",
    },
    {
      rank: "P3",
      tone: "green",
      title: "Institutionalize Monthly Insight Review",
      body: "Convert AI insights into block owner tasks with closure status.",
      badge: "Routine",
      badgeClass: "green",
    },
  ];
};

export const buildEnrolmentAccordionCopy = (overview = {}, blockData = []) => {
  const worstRetention = getWorstRetentionBlocks(blockData, 3);
  const retentionIndex =
    overview.secondary_retention_index ?? overview.secondary_hs_retention ?? "unavailable";
  const blockNames = worstRetention.map((b) => b.block_name).join(", ") || "unavailable";

  return {
    insights: `Total enrolment is ${fmt(overview.grand_total)} across ${fmt(overview.total_schools)} schools. Girls participation is ${pct(overview.girls_participation_pct)} with GPI ${overview.gender_parity_index ?? "—"}. Secondary retention index is ${retentionIndex} in the current source, and the lowest retention blocks are ${blockNames}.`,
    root: "Major signals point to transition risk between secondary and higher secondary, parity gaps in selected classes, and differentiated risk in very small and very large schools.",
    recommendations:
      "Prioritize a focused Class 10 to 11 transition intervention, add girls retention heatmaps, and separate school-size intervention strategies.",
    actions:
      "Assign block owners, set weekly review cadence, and track action closure using a simple P1/P2/P3 register.",
  };
};

export const buildEnrolmentActionRegister = (overview = {}, blockData = [], sizeDistribution = []) => {
  const blocks = getWorstRetentionBlocks(blockData, 3);
  const blockNames = blocks.map((b) => b.block_name).join(", ") || "—";
  const kpis = buildRetentionRiskKpis(overview, blockData, sizeDistribution);
  const secondaryCritical = retentionBelow(overview.secondary_hs_retention, 88);

  return [
    {
      priority: "P1",
      theme: "Retention",
      signal: "Lowest retention blocks identified",
      action: "Launch Class 10 → 11 continuation sprint",
      owner: "District Education Officer",
      status: secondaryCritical ? "Critical" : "Watch",
      statusClass: secondaryCritical ? "red" : "amber",
    },
    {
      priority: "P1",
      theme: "Block Focus",
      signal: blockNames,
      action: "Assign block-wise review targets and weekly closure tracking",
      owner: "Block Officers",
      status:
        blocks.length && secondaryCritical
          ? "Assigned"
          : blocks.length
            ? "Watch"
            : "Planned",
      statusClass: blocks.length && secondaryCritical ? "red" : blocks.length ? "amber" : "amber",
    },
    {
      priority: "P2",
      theme: "Gender Parity",
      signal: `Girls participation ${pct(overview.girls_participation_pct)}, GPI ${overview.gender_parity_index ?? "—"}`,
      action: "Create class-wise girls participation and retention heatmap",
      owner: "Inclusion Cell",
      status: "Planned",
      statusClass: "amber",
    },
    {
      priority: "P3",
      theme: "School Size",
      signal:
        kpis.smallCount || kpis.largeCount
          ? "Small and large schools need separate handling"
          : "School-size segmentation pending",
      action: "Build small-school watchlist and capacity-risk list",
      owner: "School Analytics Team",
      status: kpis.smallCount || kpis.largeCount ? "On Track" : "Planned",
      statusClass: kpis.smallCount || kpis.largeCount ? "green" : "amber",
    },
  ];
};

export const formatInsightUpdatedLabel = (isoDate) => {
  if (!isoDate) return "Ready to generate";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "Updated recently";
  const now = new Date();
  const sameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  const time = date.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
  return sameDay ? `Updated today · ${time}` : `Updated ${date.toLocaleDateString("en-IN")} · ${time}`;
};
